import { useEffect, useRef, useState, type ReactNode, type UIEvent } from "react";
import { Loader2 } from "lucide-react";
import { fetchRecoveryHierarchy } from "@/analytics/recoveryDrillThrough";
import type { RecoveryFilters } from "@/analytics/recovery.api";
import { formatCurrency } from "@/lib/tableFormatters";
import { getAriaSort, SortableColumnHeader } from "@/components/SortableColumnHeader";
import { applySort, DEFAULT_SORT, type SortDir, type SortState } from "@/lib/tableSort";

type Financials = {
  billed_net: number;
  charged: number;
  write_off: number;
};

type AssignmentNode = Financials & { assignment_name: string };
type PartnerNode    = Financials & { client_partner: string;  children: AssignmentNode[] };
type ManagerNode   = Financials & { manager: string;         children: PartnerNode[] };
type ClientNode    = Financials & { client_name: string;     children: ManagerNode[] };

type HierarchyResponse = {
  tree: ClientNode[];
  grand_total: Financials;
  client_total_rows: number;
};

type Props = { filters: RecoveryFilters };

const cx = (...cls: Array<string | false | null | undefined>) => cls.filter(Boolean).join(" ");

const TH     = "sticky top-0 z-20 whitespace-nowrap border-r border-white/10 bg-accent px-3 py-2.5 font-bold text-accent-foreground";
const TH_AMT = "sticky top-0 z-20 whitespace-nowrap bg-accent px-3 py-2.5 font-bold text-right text-accent-foreground w-32";
const AMOUNT = "px-3 py-2 text-right tabular-nums";
const COLS   = 7; // Client Name | Manager | Partner | Assignment | Billed Net | Charged | Write Off

const PAGE_SIZE = 10;


const ROW_HEIGHT_PX = 32;
const PREFETCH_ROWS_FROM_BOTTOM = 10;

type TableState = {
  tree: ClientNode[];
  grandTotal: Financials | null;
  clientTotalRows: number;
  loading: boolean;
  refetching: boolean;
  loadingMore: boolean;
  error: string | null;
};

const INITIAL_STATE: TableState = {
  tree: [],
  grandTotal: null,
  clientTotalRows: 0,
  loading: true,
  refetching: false,
  loadingMore: false,
  error: null,
};

export function RecoveryHierarchyTable({ filters }: Props) {
  const [state, setState] = useState<TableState>(INITIAL_STATE);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const onSort = (key: string, direction: SortDir | null) => setSort(applySort(key, direction));
  const loadingRef = useRef(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const ctrl = new AbortController();
    loadingRef.current = true;
    const isFirstLoad = !hasLoadedRef.current;
    setState((current) => ({
      ...current,
      loading: isFirstLoad,
      refetching: !isFirstLoad,
      loadingMore: false,
      error: null,
    }));

    fetchRecoveryHierarchy(filters, ctrl.signal, { limit: PAGE_SIZE, skip: 0 }, sort)
      .then((res) => {
        if (ctrl.signal.aborted) return;
        hasLoadedRef.current = true;
        const response = res as HierarchyResponse;
        setState({
          tree: response.tree,
          grandTotal: response.grand_total,
          clientTotalRows: response.client_total_rows,
          loading: false,
          refetching: false,
          loadingMore: false,
          error: null,
        });
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setState((current) => ({
          ...current,
          loading: false,
          refetching: false,
          loadingMore: false,
          error: err instanceof Error ? err.message : "Unable to load data",
        }));
      })
      .finally(() => { loadingRef.current = false; });

    return () => ctrl.abort();
  }, [filters, sort]);

  const hasMore = state.tree.length < state.clientTotalRows;

  const loadMore = () => {
    if (loadingRef.current || state.refetching || !hasMore) return;
    loadingRef.current = true;
    setState((current) => ({ ...current, loadingMore: true }));

    fetchRecoveryHierarchy(filters, undefined, { limit: PAGE_SIZE, skip: state.tree.length }, sort)
      .then((res) => {
        const response = res as HierarchyResponse;
        setState((current) => ({
          ...current,
          tree: [...current.tree, ...response.tree],
          grandTotal: response.grand_total,
          clientTotalRows: response.client_total_rows,
          loadingMore: false,
        }));
      })
      .catch(() => {
        setState((current) => ({ ...current, loadingMore: false }));
      })
      .finally(() => { loadingRef.current = false; });
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasMore || state.loading || state.refetching || state.loadingMore) return;
    const el = event.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= PREFETCH_ROWS_FROM_BOTTOM * ROW_HEIGHT_PX) {
      loadMore();
    }
  };

  const { tree, grandTotal, clientTotalRows, loading, refetching, loadingMore, error } = state;

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-sm" aria-label="Recovery client hierarchy">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold text-card-foreground">Recovery by Client / Manager / Partner</h2>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          {refetching ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : null}
          {loading ? "Loading" : `${tree.length} / ${clientTotalRows} clients`}
        </span>
      </div>

      <div
        className={cx("max-h-[620px] overflow-auto", refetching && "opacity-60 transition-opacity")}
        onScroll={handleScroll}
      >
        <table className="w-full min-w-[900px] table-auto border-separate border-spacing-0 text-xs">
          <thead>
            <tr>
              <th
                className={cx(TH, "sticky left-0 z-30 text-left w-48")}
                aria-sort={getAriaSort("client_name", sort)}
              >
                <SortableColumnHeader label="Client Name" sortKey="client_name" sort={sort} onSort={onSort} />
              </th>
              <th className={cx(TH, "text-left w-40")}>Assignment Manager</th>
              <th className={cx(TH, "text-left w-40")}>Client Partner</th>
              <th className={cx(TH, "text-left")}>Assignment Name</th>
              <th className={TH_AMT} aria-sort={getAriaSort("billed_net", sort)}>
                <SortableColumnHeader label="Billed Net" sortKey="billed_net" sort={sort} onSort={onSort} />
              </th>
              <th className={TH_AMT} aria-sort={getAriaSort("charged", sort)}>
                <SortableColumnHeader label="Charged" sortKey="charged" sort={sort} onSort={onSort} />
              </th>
              <th className={TH_AMT} aria-sort={getAriaSort("write_off", sort)}>
                <SortableColumnHeader label="Write Off" sortKey="write_off" sort={sort} onSort={onSort} />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td className="h-44 bg-card text-center text-muted-foreground" colSpan={COLS}>Loading data…</td></tr>
            )}
            {!loading && error && tree.length === 0 && (
              <tr><td className="h-44 bg-card text-center text-destructive" colSpan={COLS}>{error}</td></tr>
            )}
            {!loading && !error && tree.length === 0 && (
              <tr><td className="h-44 bg-card text-center text-muted-foreground" colSpan={COLS}>No records found for this month</td></tr>
            )}

            {!loading && tree.map((client, cIdx) => (
              <ClientBlock key={`${client.client_name}::${cIdx}`} client={client} isLast={cIdx === tree.length - 1} />
            ))}

            {!loading && loadingMore && (
              <tr>
                <td className="border-b bg-card px-3 py-2.5 text-center text-muted-foreground" colSpan={COLS}>
                  <span className="inline-flex items-center gap-2 text-xs">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading more clients...
                  </span>
                </td>
              </tr>
            )}

            {!loading && grandTotal && tree.length > 0 && (
              <tr className="bg-accent/60 font-bold">
                <td
                  className="sticky left-0 z-10 border-t-2 border-border px-3 py-2.5 text-foreground bg-accent/60 shadow-[1px_0_0_hsl(var(--border))]"
                  colSpan={4}
                >
                  Grand Total
                </td>
                <td className={cx(AMOUNT, "border-t-2 border-border py-2.5 font-bold")}>
                  {formatCurrency(grandTotal.billed_net)}
                </td>
                <td className={cx(AMOUNT, "border-t-2 border-border py-2.5 font-bold")}>
                  {formatCurrency(grandTotal.charged)}
                </td>
                <td className={cx(AMOUNT, "border-t-2 border-border py-2.5 font-bold")}>
                  {formatCurrency(grandTotal.write_off)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}


function ClientBlock({ client, isLast }: { client: ClientNode; isLast: boolean }) {
  const totalAssignments = client.children.reduce(
    (sum, manager) => sum + manager.children.reduce((s, partner) => s + partner.children.length, 0),
    0,
  );
  const clientRowSpan = totalAssignments + 1;

  const clientNameCell = (
    <td
      rowSpan={clientRowSpan}
      className="sticky left-0 z-10 border-b border-border/60 px-3 py-2 align-top font-semibold text-foreground bg-muted/40 shadow-[1px_0_0_hsl(var(--border))] whitespace-nowrap"
    >
      {client.client_name}
    </td>
  );

  const assignmentRows: ReactNode[] = [];
  let isFirstRowOfClient = true;

  client.children.forEach((manager) => {
    manager.children.forEach((partner, pIdx) => {
      partner.children.forEach((assignment, aIdx) => {
        const isFirstOfManager = pIdx === 0 && aIdx === 0;
        const isFirstOfPartner = aIdx === 0;

        assignmentRows.push(
          <AssignmentRow
            key={`${manager.manager}::${partner.client_partner}::${assignment.assignment_name}::${aIdx}`}
            clientNameCell={isFirstRowOfClient ? clientNameCell : null}
            managerName={isFirstOfManager ? manager.manager : ""}
            partnerName={isFirstOfPartner ? partner.client_partner : ""}
            assignmentName={assignment.assignment_name}
            billedNet={assignment.billed_net}
            charged={assignment.charged}
            writeOff={assignment.write_off}
            shade={aIdx % 2 === 1}
          />,
        );
        isFirstRowOfClient = false;
      });
    });
  });

  return (
    <>
      {assignmentRows}

      <tr className="bg-accent/20 font-semibold">
        {totalAssignments === 0 ? clientNameCell : null}
        <td className="border-b border-border/60 px-3 py-2" />
        <td className="border-b border-border/60 px-3 py-2" />
        <td className="border-b border-border/60 px-3 py-2 text-foreground">Total Billed</td>
        <td className={cx(AMOUNT, "border-b border-border/60 font-semibold")}>{formatCurrency(client.billed_net)}</td>
        <td className={cx(AMOUNT, "border-b border-border/60 font-semibold")}>{formatCurrency(client.charged)}</td>
        <td className={cx(AMOUNT, "border-b border-border/60 font-semibold")}>{formatCurrency(client.write_off)}</td>
      </tr>

      {!isLast && (
        <tr className="h-1 bg-background">
          <td colSpan={COLS} />
        </tr>
      )}
    </>
  );
}


type AssignmentRowProps = {
  clientNameCell: ReactNode;
  managerName: string;
  partnerName: string;
  assignmentName: string;
  billedNet: number;
  charged: number;
  writeOff: number;
  shade: boolean;
};

function AssignmentRow({ clientNameCell, managerName, partnerName, assignmentName, billedNet, charged, writeOff, shade }: AssignmentRowProps) {
  return (
    <tr className={cx("hover:bg-secondary/40", shade && "bg-muted/20")}>
      {clientNameCell}
      <td className="border-b border-border/30 px-3 py-2 text-foreground whitespace-nowrap">
        {managerName}
      </td>
      <td className="border-b border-border/30 px-3 py-2 text-foreground whitespace-nowrap">
        {partnerName}
      </td>
      <td className="border-b border-border/30 px-3 py-2 text-foreground">
        {assignmentName}
      </td>
      <td className={cx(AMOUNT, "border-b border-border/30")}>{formatCurrency(billedNet)}</td>
      <td className={cx(AMOUNT, "border-b border-border/30")}>{formatCurrency(charged)}</td>
      <td className={cx(AMOUNT, "border-b border-border/30")}>{formatCurrency(writeOff)}</td>
    </tr>
  );
}
